import type { SwapTokenInfo } from '@/hooks/use-tokens'
import { Candle, CandleSequence } from '@/providers/liquidity-chart/candle-utils'

import { orderTokenInfosByUiQuote } from '../limit-order/utils'

export const mergeOrStartNewCandle = (
  lastCandle: Candle | undefined,
  newData: { price: number; time: Date; volume: number },
  span: number,
): Candle => {
  const clampedStartTime = CandleSequence.clampStartTimeBySpan(newData.time, span)

  if (lastCandle && lastCandle.startTime.getTime() === clampedStartTime.getTime()) {
    return {
      ...lastCandle,
      close: newData.price,
      low: Math.min(lastCandle.low, newData.price),
      high: Math.max(lastCandle.high, newData.price),
      volume: newData.volume,
    }
  } else {
    return {
      startTime: clampedStartTime,
      span,
      open: newData.price,
      close: newData.price,
      low: newData.price,
      high: newData.price,
      volume: newData.volume,
    }
  }
}

/**
 * Replicates backend logic of sorting a token pair.
 *
 * @returns `[base, quote]`
 *
 * @see https://github.com/renec-chain/renechub/blob/e10f8fca45cf006186de721d8ccd76ea6375266a/app/helpers/nemoswap/market_price_tracking_helper.rb
 */
export const orderTokensLikeBE = (tokenA: SwapTokenInfo, tokenB: SwapTokenInfo) => {
  const [baseToken, quoteToken] = [tokenA, tokenB].sort((a, b) =>
    a.symbol.toLowerCase().localeCompare(b.symbol.toLowerCase()),
  )
  return orderTokenInfosByUiQuote([baseToken, quoteToken], [tokenA, tokenB])
}

export const getQueryKeyTickers = (tokenA?: SwapTokenInfo, tokenB?: SwapTokenInfo, span?: number) => {
  return ['tickers', [tokenA?.symbol, tokenB?.symbol].sort().join(), span]
}

export const getQueryOptionsTickers = (spanInSeconds: number) => ({
  cacheTime: Infinity,
  staleTime: spanInSeconds * 1000,
})
